import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { API_BASE } from './api';
import { DEFAULT_TEXTS, formatDisplayText } from './displayLayoutModel';
import DisplayIcon from './DisplayIcon';
import './GateDisplay.css';

const PACKING_COLOR = '#F28C00';
export const APPEARANCE = {
  FREE: ['free', '#2E9B61'], PACKEN: ['packen', PACKING_COLOR], PACKED: ['packed', PACKING_COLOR], STORAGE: ['storage', '#7656D6'],
  LOADING: ['loading', '#087EEA'], EMPTY_CASES: ['emptyCases', '#0096A6'], RETURN_FULL: ['returnFull', '#8B5CF6'],
  EVENT: ['event', '#DB2777'], BLOCKED: ['blocked', '#B3261E'], OUT_OF_SERVICE: ['outOfService', '#666B73'],
};
export function getDisplayContext() {
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname;
  const match = path.match(/^\/display\/(gate|loadingbay)\/(\d+)$/) || path.match(/^\/display\/(\d+)$/);
  return {
    resourceType: path.includes('/display/loadingbay') ? 'loadingBay' : 'gate',
    resourceId: Number(match ? match[2] || match[1] : params.get('id') || params.get('gateId') || params.get('gate') || params.get('loadingBayId') || params.get('loadingbay')),
  };
}
const unique = values => [...new Set(values.filter(v => typeof v === 'string' && v.trim()).map(v => v.trim()))];
export const jobName = slot => unique([slot?.job_name, slot?.custom_job_name, ...(slot?.jobs || []).map(job => job.job_name || job.name)]).join(', ');
function timeLabel(unix, now) {
  const date = new Date(Number(unix) * 1000);
  if (!Number.isFinite(date.getTime())) return '';
  const time = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return date.toDateString() === new Date(now).toDateString() ? time : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + ' · ' + time;
}
function validSlot(slot) {
  return slot === null || (slot && Number.isInteger(Number(slot.id)) && Number(slot.end_time) > Number(slot.start_time) && Array.isArray(slot.jobs));
}
export function validSnapshot(data, context) {
  return Boolean(data && data.resource && data.resource.resourceType === context.resourceType &&
    Number(data.resource.resourceId) === context.resourceId && typeof data.resource.is_active === 'boolean' &&
    APPEARANCE[data.status] && validSlot(data.active) && validSlot(data.next) && (!data.next || APPEARANCE[data.next.display_status]) &&
    Array.isArray(data.relations) && data.relations.every(r => Number.isInteger(Number(r.sourceSlotId)) && Number.isInteger(Number(r.targetSlotId))) &&
    data.texts && Object.keys(DEFAULT_TEXTS).every(key => typeof data.texts[key] === 'string' && data.texts[key].trim()) &&
    Number.isFinite(Date.parse(data.generatedAt)));
}

export function DisplayView({ snapshot, context, now, connectionLost = false, errorKey = '' }) {
  const stageRef = useRef(null);
  const [scale, setScale] = useState(() => Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
  const texts = { ...DEFAULT_TEXTS, ...snapshot?.texts };
  const t = (key, values) => formatDisplayText(texts['display.' + key], values);
  const locationName = resource => resource.resourceType === 'storage' ? 'LAGER' : (resource.resourceType === 'area' ? 'Fläche' : t(resource.resourceType === 'loadingBay' ? 'type.loadingZone' : 'type.gate')) + ' ' + resource.resourceId;
  const statusOf = slot => slot === snapshot?.active ? snapshot.status : slot.display_status;
  const priority = slot => ({ OUT_OF_SERVICE: 3, BLOCKED: 2, DELAYED: 1 }[statusOf(slot)] || 0);
  const active = [snapshot?.active, snapshot?.next]
    .filter(slot => slot && Number(slot.start_time) * 1000 <= now && Number(slot.display_until ?? slot.end_time) * 1000 > now)
    .sort((a, b) => priority(b) - priority(a) || Number(b.start_time) - Number(a.start_time) || Number(a.id) - Number(b.id))[0] || null;
  const next = snapshot?.next && Number(snapshot.next.start_time) * 1000 > now ? snapshot.next : null;
  const status = snapshot?.resource.is_active === false ? 'OUT_OF_SERVICE' : active ? statusOf(active) : 'FREE';
  const [statusKey, color] = APPEARANCE[status] || APPEARANCE.OUT_OF_SERVICE;
  const relation = active && snapshot?.relations.find(r => Number(r.sourceSlotId) === Number(active.id) || Number(r.targetSlotId) === Number(active.id));
  const isSource = relation && Number(relation.sourceSlotId) === Number(active.id);
  const eventKey = active?.self_pickup && status === 'LOADING' ? 'customerPickup' : active?.self_pickup && status === 'RETURN_FULL' ? 'customerReturn' : ({ PACKEN: 'staged', PACKED: 'staged', STORAGE: 'waiting', LOADING: 'loading', EMPTY_CASES: 'emptyReturn', RETURN_FULL: 'return', EVENT: 'waiting' })[status] || null;
  const elapsed = active ? Math.max(0, Math.floor((now / 1000 - Number(active.start_time)) / 60)) : 0;
  const time = active ? timeLabel(active.start_time, now) + ' – ' + timeLabel(active.end_time, now) : '';
  const details = active ? [
    ['customer', unique((active.jobs || []).map(job => job.customer)).join(', ')],
    ['driver', active.driver_name],
    ['vehicle', unique([active.vehicle_class_name, active.license_plate]).join(' · ')],
    ['time', status === 'DELAYED' && elapsed > 0 ? time + ' · ' + t('time.overdue', { minutes: elapsed }) : time],
    ['event', String(active.notes || '').trim() || (eventKey ? t('event.' + eventKey) : '')],
  ].filter(([, value]) => typeof value === 'string' && value.trim()) : [];
  const contentKey = JSON.stringify([snapshot, Boolean(active), Boolean(next), elapsed, errorKey, connectionLost]);

  useEffect(() => {
    const resize = () => setScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const fitText = (selector, initial, minimum) => {
      const el = stage.querySelector(selector);
      if (!el) return;
      el.style.fontSize = initial + 'px';
      while ((el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1) && parseFloat(el.style.fontSize) > minimum) {
        el.style.fontSize = (parseFloat(el.style.fontSize) - 2) + 'px';
      }
    };
    fitText('.tv-location', 80, 48);
    fitText('.tv-status-text', 96, 48);
    fitText('.tv-job', 100, 56);
    stage.querySelectorAll('[data-priority]').forEach(el => { el.style.display = ''; });
    // Keep primary operational information large before sacrificing lower-priority rows.
    const removable = [...stage.querySelectorAll('[data-priority]')].sort((a, b) => Number(a.dataset.priority) - Number(b.dataset.priority));
    for (const el of removable) {
      if (stage.scrollHeight <= stage.clientHeight + 1) break;
      el.style.display = 'none';
    }
  }, [contentKey, scale]);

  const message = errorKey ? t('system.' + errorKey) : !snapshot ? t('system.loading') : '';
  return <main className="tv-display" style={{ backgroundColor: snapshot ? color : '#666B73' }}>
    <div className="tv-stage" ref={stageRef} style={{ transform: 'translate(-50%, -50%) scale(' + scale + ')' }}>
      <header className="tv-location">{locationName(context)}</header>
      {message ? <div className="tv-message" role="alert">{message}</div> : <>
        <div className="tv-status"><DisplayIcon name={status} /><span className="tv-status-text">{t('status.' + statusKey)}</span></div>
        {active ? <>
          {jobName(active) && <h1 className="tv-job">{jobName(active)}</h1>}
          <dl className="tv-details">{details.map(([key, value]) => <div key={key} className={'tv-detail tv-detail-' + key} data-priority={{ event: 1, customer: 2, driver: 3, vehicle: 5, time: 6 }[key]}>
            <dt>{t('label.' + key)}</dt><dd>{value}</dd>
          </div>)}</dl>
          {relation && <section className="tv-process" data-priority="4" aria-label={t('link.nextStep')}>
            <DisplayIcon name="link" />
            <div>{t(isSource ? 'link.linkedWith' : 'link.materialFrom')} {locationName(isSource ? relation.target : relation.source)}</div>
            <DisplayIcon name="arrow" />
            <div>{isSource ? t('link.nextOutgoing') + ' ' + locationName(relation.target) : t('link.routeFromTo', { from: locationName(relation.source), to: locationName(relation.target) })}</div>
          </section>}
        </> : status === 'FREE' && next ? <section className="tv-next">
          <h2>{t('time.nextOccupancy')}</h2>
          <p>{t('time.from')} {timeLabel(next.start_time, now)}</p>
          {Number(next.start_time) * 1000 - now <= 60 * 60 * 1000 && <p className="tv-countdown">{t('time.inMinutes', { minutes: Math.max(1, Math.ceil((Number(next.start_time) * 1000 - now) / 60000)) })}</p>}
          <h1 className="tv-job">{jobName(next)}</h1>
        </section> : null}
      </>}
      {snapshot && !connectionLost && !message && <div className="tv-live">{t('system.live')}</div>}
    </div>
    {connectionLost && <div className="tv-connection" role="alert"><DisplayIcon name="DELAYED" /><span>{t('system.connectionLost')}</span></div>}
  </main>;
}

export default function GateDisplay() {
  const [context] = useState(getDisplayContext);
  const [snapshot, setSnapshot] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [offline, setOffline] = useState(!navigator.onLine);
  const [stale, setStale] = useState(false);
  const [errorKey, setErrorKey] = useState('');
  const invalid = !Number.isInteger(context.resourceId) || context.resourceId <= 0;
  useEffect(() => {
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = oldOverflow; };
  }, []);
  useEffect(() => {
    if (invalid) return undefined;
    let disposed = false;
    let controller = null;
    let lastSuccess = performance.now();
    let lastGeneratedAt = 0;
    async function refresh() {
      if (disposed || controller) return;
      controller = new AbortController();
      const current = controller;
      const timeout = window.setTimeout(() => current.abort(), 8000);
      try {
        const response = await fetch(API_BASE + '/public/display-state?resourceType=' + context.resourceType + '&resourceId=' + context.resourceId, { signal: current.signal, cache: 'no-store' });
        if (!response.ok) {
          if (!disposed && [400, 403, 404].includes(response.status)) setErrorKey(response.status === 404 ? 'unknownResource' : response.status === 400 ? 'invalidResource' : 'unavailable');
          throw new Error('Display request failed');
        }
        const data = await response.json();
        if (!validSnapshot(data, context) || Date.parse(data.generatedAt) <= lastGeneratedAt) throw new Error('Invalid or outdated display data');
        if (disposed || current.signal.aborted) return;
        lastGeneratedAt = Date.parse(data.generatedAt);
        lastSuccess = performance.now();
        setSnapshot(data); setErrorKey(''); setStale(false);
      } catch {
        // Preserve the last valid state. The freshness clock triggers the interruption screen.
      } finally {
        window.clearTimeout(timeout);
        if (controller === current) controller = null;
      }
    }
    const clock = window.setInterval(() => { setNow(Date.now()); setStale(performance.now() - lastSuccess >= 15000); }, 250);
    const poll = window.setInterval(refresh, 5000);
    const online = () => { setOffline(false); refresh(); };
    const offlineHandler = () => setOffline(true);
    const visible = () => { if (!document.hidden) refresh(); };
    window.addEventListener('online', online);
    window.addEventListener('offline', offlineHandler);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', visible);
    refresh();
    return () => {
      disposed = true; controller?.abort();
      window.clearInterval(clock); window.clearInterval(poll);
      window.removeEventListener('online', online); window.removeEventListener('offline', offlineHandler);
      window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', visible);
    };
  }, [context, invalid]);
  return <DisplayView snapshot={snapshot} context={context} now={now} connectionLost={!invalid && !errorKey && (offline || stale)} errorKey={invalid ? 'invalidResource' : errorKey} />;
}
